import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  ScrollArea,
  rem,
  Text,
  UnstyledButton,
  Group,
  Center,
  Select,
  Button,
  Pagination,
  Checkbox,
  Loader,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
  IconEdit,
  IconFileExport,
  IconEye,
} from "@tabler/icons-react";
import AddFeedModal from "./AddFeedModal";
import EditFeedModal from "./EditFeedModal";
import DeleteModal from "./DeleteModal";
import ViewFeedModal from "./ViewFeedModal";
import classes from "../MainPage/mainpage.module.css";
import { RowDataMain } from "./types";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { main } from "../types/RowDataTypes";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import { CustomTable } from "../Table/CustomTable";
import { showErrorAlert, showSuccessAlert } from "../../helper/alerts";
import { SearchBar } from "../SearchBar/SearchBar";
import Header from "../static/Header";
import getMainTable from "../../DataDisplay/DataDisplayTable/MainTable";

// Searching and Sorting
enum ColumnHeaderToFilterPair {
  TITLE = "Title",
  THUMBNAIL = "Thumbnail URL",
  VIDEO = "Video URL",
  CATEGORY = "Category",
  SHOW = "Show",
}

const colFilter = ColumnHeaderToFilterPair;
const colFilters: { [key in ColumnHeaderToFilterPair]: string } = {
  [colFilter.TITLE]: "title",
  [colFilter.THUMBNAIL]: "",
  [colFilter.VIDEO]: "",
  [colFilter.CATEGORY]: "",
  [colFilter.SHOW]: "show",
};

interface ThProps {
  children: React.ReactNode;
  sortable: boolean;
  reversed: boolean;
  sorted: boolean;
  onSort(): void;
}

function Th({ children, sortable, reversed, sorted, onSort }: ThProps) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  const allowedSorts = [
    colFilter.TITLE.toLowerCase(),
    colFilter.SHOW.toLowerCase(),
  ];

  return (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      {allowedSorts.includes(children!!.toString().toLowerCase()) ? (
        <UnstyledButton onClick={onSort} className={classes.control}>
          <Group justify="space-between">
            <Text className={classes.Text}>{children}</Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

const initialData: RowDataMain[] = [
  {
    id: "",
    title: "",
    thumbnail: "",
    video: "",
    category: 0,
    show: false,
    action: "",
  },
];

export function HomePage() {
  const axiosPrivate = useAxiosPrivate();
  const [isLoading, setIsLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [tableData, setTableData] = useState(initialData);

  const [isAddFeedModalOpen, setAddFeedModalOpen] = useState(false);
  const [isEditFeedModalOpen, setEditFeedModalOpen] = useState(false);
  const [selectedRowForEdit, setSelectedRowForEdit] =
    useState<RowDataMain | null>(null);
  const [isViewFeedModalOpen, setViewFeedModalOpen] = useState(false);
  const [selectedRowForView, setSelectedRowForView] =
    useState<RowDataMain | null>(null);

  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] =
    useState<RowDataMain | null>(null);

  const [catList, setCatList] = useState<string[]>([]);

  const handleAddFeed = () => {
    setAddFeedModalOpen(true);
  };

  const handleAddFeedModalClose = () => {
    setAddFeedModalOpen(false);
  };

  const fetchCategory = async () => {
    try {
      const response = await axiosPrivate.get("/api/feed/read-category"); 
      const resDataArr = response.data.data;
      setCatList(["", resDataArr[0].name, resDataArr[1].name, resDataArr[2].name])
    } catch (err) {
      console.error("Failed to fetch contact [ERROR]:", err);
    } finally {
    }
  };

  const handleAddFeedSuccess = async (newFeedData: RowDataMain) => {
    setIsLoading(true);
    await axiosPrivate
    .post(
        "/api/admin/feed/add",
        {
        title: newFeedData.title,
        category: newFeedData.category,
        video: newFeedData.video,
        thumbnail: newFeedData.thumbnail,
        },
        {
        headers: {
            "Content-Type": "multipart/form-data",
        },
        }
    )
    .then((response) => {
        if (itemsOnPage.current < itemsPerPage) {
        const data = response.data.links;
        const returnedData: RowDataMain = {
            ...newFeedData,
            id: data.id,
            video: data.videoSrc,
            thumbnail: data.thumbnailSrc,
            category: data.category,
        };

        if (!reverseSortDirection)
            setRowDataMain((prevData) => [returnedData, ...prevData!!]);
        else setRowDataMain((prevData) => [...prevData!!, returnedData]);
        }
        itemsOnPage.current = itemsOnPage.current + 1;
        setTotalRows((prevData) => prevData + 1);
        setIsLoading(false);
        showSuccessAlert("Feed Successfully Added");
    })
    .catch((error) => {
        console.log("error " + error);
        setIsLoading(false);
        showErrorAlert("Something Went Wrong");
    });
  };

  const handleEdit = (row: RowDataMain) => {
    setSelectedRowForEdit(row);
    setEditFeedModalOpen(true);
  };

  const handleEditFeedSuccess = (editedFeedData: RowDataMain) => {
    let params: any = {
      id: editedFeedData.id,
      title: editedFeedData.title,
      category: editedFeedData.category,
    };
    if (!!editedFeedData.thumbnail.path)
      params["thumbnail"] = editedFeedData.thumbnail;

    if (!!editedFeedData.video.path) params["video"] = editedFeedData.video;

    axiosPrivate
      .post("/api/admin/feed/update", params, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        console.log("edited data");
        console.log(response);
        // Find the index of the selectedRow in the sortedData
        const indexOfSelectedRow = rowDataMain!!.findIndex(
          (row) => row === selectedRowForEdit
        );

        // Create a copy of the sortedData
        const updatedData = [...rowDataMain!!];
        const data = response.data.links;
        // Update the selectedRow in the copied data

        let dataToUpdate: any = {
          ...selectedRowForEdit!!,
          title: editedFeedData.title,
        };

        if (!!data.videoSrc) dataToUpdate.video = data.videoSrc;

        if (!!data.thumbnailSrc) dataToUpdate.thumbnail = data.thumbnailSrc;

        updatedData[indexOfSelectedRow] = dataToUpdate;
        // changeTableState();
        setRowDataMain(updatedData);
        showSuccessAlert("Feed Successfully Edited");
      })
      .catch((error) => {
        console.log("error " + error);
        showErrorAlert("Something Went Wrong");
      });

    const updatedData = tableData.map((item) =>
      item === selectedRowForEdit ? editedFeedData : item
    );
    setTableData(updatedData);
    setEditFeedModalOpen(false);
  };

  const handleDelete = (row: RowDataMain) => {
    setSelectedRowToDelete(row);
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmed = () => {
    axiosPrivate
      .post("/api/admin/feed/delete", {
        id: selectedRowToDelete!!.id,
      })
      .then((response) => {
        setDeleteConfirmationOpen(false);
        if (
          itemsOnPage.current == 1 &&
          currentPage !== 1 &&
          currentPage == Math.ceil(totalRows / itemsPerPage)
        )
          setCurrentPage((prev) => prev - 1);
        else changeTableState();
      })
      .catch((err) => {
        showErrorAlert("An error has occured");
      });
    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleDeleteCancelled = () => {
    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleView = (row: RowDataMain) => {
    setSelectedRowForView(row);
    setViewFeedModalOpen(true);
  };

  // Sorting
  const [sortBy, setSortBy] = useState<keyof RowDataMain | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const itemsOnPage = useRef(0);

  // Table state change trigger
  const [tableStateChanged, setTableStateChanged] = useState(0);

  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [colFilters[colFilter.TITLE]];
  for (const [key, value] of Object.entries(colFilters)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };

  const setSorting = (field: keyof RowDataMain) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    changeTableState();
  };

  //For hiding/displaying the search filter options
  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  const resetPage = () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  };

  const toggleVisibility = async (row: RowDataMain, e: any) => {
    if (e.target.tagName === "SPAN") e = e.target.parentElement.parentElement;
    else e = e.target;
    e.disabled = true;
    let endpoint;
    if (!row.show) endpoint = "/api/admin/feed/show";
    else endpoint = "/api/admin/feed/hide";

    await axiosPrivate
      .post(endpoint, {
        id: row.id,
      })
      .then((response) => {
        const indexOfSelectedRow = rowDataMain!!.findIndex((e) => e === row);

        // Create a copy of the sortedData
        const updatedData = [...rowDataMain!!];

        // Update the selectedRow in the copied data
        updatedData[indexOfSelectedRow] = {
          ...row,
          show: !row.show,
        };

        setRowDataMain(updatedData);
      })
      .catch((err) => {
        showErrorAlert("An error occured");
      });

    e.disabled = false;
  };

  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    main,
    getMainTable().create()
  );

  const [totalRows, setTotalRows] = useState<number>(0);
  const rowData: RowDataMain[] | null = fetchedData.data as RowDataMain[];
//   const isLoading: boolean = fetchedData.isLoading;
  const [rowDataMain, setRowDataMain] = useState<RowDataMain[] | null>(null);

  const startIndex = (currentPage - 1) * itemsPerPage;

  useEffect(() => {
    fetchCategory();
    setRowDataMain(rowData);
    itemsOnPage.current = fetchedData.itemsOnPage;
    setTotalRows(fetchedData.totalRows);
  }, [rowData]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const rowsToMap = rowDataMain ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td className={classes.titleColumn}>{row.title}</Table.Td>
      <Table.Td className={classes.UrlColumn}>{row.thumbnail}</Table.Td>
      <Table.Td className={classes.UrlColumn}>{row.video}</Table.Td>
      <Table.Td className={classes.buttonColumn}>{catList[Number(row.category)]}</Table.Td>
      <Table.Td className={classes.buttonColumn}>
        <Button
          style={{
            backgroundColor: row.show ? "#2BDD66" : "#0014AD",
          }}
          onClick={(e) => toggleVisibility(row, e)}
        >
          {row.show ? "ON" : "OFF"}
        </Button>
      </Table.Td>
      <Table.Td className={classes.actionButtons}>
        <button onClick={() => handleView(row)} className={classes.viewButton}>
          <IconEye /> View
        </button>

        <button onClick={() => handleEdit(row)} className={classes.editbutton}>
          <IconEdit /> Edit
        </button>

        <button
          onClick={() => handleDelete(row)}
          className={classes.trashbutton}
        >
          <IconTrash /> Delete
        </button>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage !== currentPage) setCurrentPage(selectedPage);
  };

  return (
    <div className={classes.homeContainer}>
    {isLoading && (
        <div className={classes.loadingOverlay}>
          <Loader size="xl" />
        </div>
      )}
      <ScrollArea className={classes.parentContainer}>
        <Header title="Home Feed" />
        <div className={classes.searchAndAddContainer}>
          <SearchBar
            isLoading={isLoading}
            resetPage={resetPage}
            changeTableState={changeTableState}
            search={search}
            handleSearch={handleSearch}
            setShowFilterOptions={setShowFilterOptions}
            showFilterOptions={showFilterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            checkboxesFilter={checkboxesFilter}
          />
          <div className={classes.AddButtonContainer}>
            <Button className={classes.addButton} onClick={handleAddFeed}>
              <IconFileExport className={classes.iconFileExport} />
              Create Feed
            </Button>
          </div>
        </div>

        <AddFeedModal
          isOpen={isAddFeedModalOpen}
          onClose={handleAddFeedModalClose}
          onAddFeed={handleAddFeedSuccess}
        />

        <EditFeedModal
          isOpen={isEditFeedModalOpen}
          onClose={() => setEditFeedModalOpen(false)}
          onEditFeed={handleEditFeedSuccess}
          feedData={selectedRowForEdit || initialData[0]}
        />

        <DeleteModal
          isOpen={isDeleteConfirmationOpen}
          onClose={handleDeleteCancelled}
          onConfirm={handleDeleteConfirmed}
        />

        <ViewFeedModal
          isOpen={isViewFeedModalOpen}
          onClose={() => setViewFeedModalOpen(false)}
          feedData={selectedRowForView || initialData[0]}
        />
        <ScrollArea
          w={
            !(!document.getElementById("nav-bar")) ? window.innerWidth -
              document.getElementById("nav-bar")!!.getBoundingClientRect().width : 1000
          }
        >
          <CustomTable
            colHeaders={Object.values(ColumnHeaderToFilterPair)}
            filters={Object.values(colFilters)}
            reverseSortDirection={reverseSortDirection}
            sortBy={sortBy}
            setSorting={setSorting}
            customclasses={classes}
            Th={Th}
            rowData={rowDataMain}
            rows={rows}
          />
        </ScrollArea>

        <div className={classes.paginationContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results`}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

export default HomePage;
